import { BaseLayout } from '@hub/layout/BaseLayout/BaseLayout';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'next-i18next';
import { FreeFormHubTable } from './product-table/FreeFormHubTable';
import type { NodeContact } from '@hub/models/api/node-contact.types';
import { FilterForm } from './product-filters/components/filterForm/filterForm';
import { useRouter } from 'next/router';
import { type freeFormFilterValues, freeFormHubFilterKeys } from './product-filters/types';
import { useFilteredProductList } from './hooks/useFilteredProductList';
import { supportedFileFormat } from '@hub/common/ModelViewer/ModelViewer';
import { useSelectedMaterialColor } from './hooks/useSelectedMaterialColor';
import { Link } from '@hub/common/Link/Link';
import { useSessionStore } from '@hub/hooks/states/useSessionStore';
import { useMaterials } from './hooks/useMaterials';
import { FreeFormHubProductTile } from '@hub/free-form-hub/free-form-hub-result/product-tile/FreeFormHubProductTile';
import { CNC_FILE_SIZE_LIMIT, DEFAULT_QUANTITY, QUANTITY_LIMIT } from '@hub/free-form-hub/common/constants';
import { SeoMeta } from '@hub/common/SeoMeta/SeoMeta';
import { DropZone } from '@hub/free-form-hub/common/uploadFile/Dropzone';
import { useLoader } from '@hub/free-form-hub/common/loader/useLoader';
import { usePiwikPro } from '@piwikpro/next-piwik-pro';
import { ffhEventCategory, TrackedFfhEventActions } from '@hub/free-form-hub/tracking.config';
import { useCopyLinkToClipboard } from '@hub/hooks/useCopyToClipboard';
import { Icon, IconColor, IconID } from '@hub/common/Icon/Icon';
import { LifetimeParametersForm } from '@hub/free-form-hub/common/lifetime/LifetimeParametersForm';
import { useLifeTime } from '@hub/free-form-hub/common/lifetime/useLifetime';
import { SpatialTypeOfMovementSingle } from '@igus/tribology-domain/src/lib/wear/wear.definitions';
import { BasicNumberInput } from '@hub/common/BasicNumberInput/BasicNumberInput';
import { useNbsRecommendation } from '@hub/free-form-hub/common/recommendations/useNbsRecommendation';
import { type ToastPosition, ToastType, useToasts } from '@hub/hooks/useToasts';
import type { Product } from '@igus/iglidur-free-form-hub-interfaces';
import { displayPrice } from '@hub/libs/product-price-utils';
import dynamic from 'next/dynamic';
import { useFfhUpload } from '@hub/free-form-hub/common/uploadFile/useFfhUpload';
import { useStreamProductsData } from '@hub/free-form-hub/free-form-hub-result/hooks/useStreamProductsData';
import { AreaCode, getUnitLabelForUnitSystem, LengthCode } from '@icommerce/units';
import { SUPPORTED_IGLIDE_IBH_LOCALES } from '@hub/libs/bearing-hub.helper';
import { fromMetricDistanceMilli, fromMetricVolumeMilli, UnitSystem } from '@igus/kopla-domain';
import { Collapsible } from '@hub/common/Collapsible/Collapsible';
import { CollapsibleTheme } from '@hub/common/Collapsible/BaseCollapsible';
import { ProgressBar } from '@hub/common/ProgressBar/ProgressBar';
import { FREE_FORM_HUB_URL } from '@hub/free-form-hub/config';
import { IglideStandardModal } from '@hub/common/IglideStandardModal/IglideStandardModal';
import { FilterResetLink } from '@hub/common/FilterResetLink/FilterResetLink';
import { UploadProcessImages } from '@hub/free-form-hub/common/uploadFile/UploadProcessImages';
import { NbsRecommendationDialog } from '@hub/free-form-hub/common/recommendations/NbsRecommendationDialog';
import { SampleDialog } from '@hub/free-form-hub/common/uploadFile/SampleDialog';
import { useSampleModel } from '@hub/free-form-hub/common/uploadFile/useSampleModel';
import { EXTERNAL_LINKS } from '@hub/libs/redirect.helper';
import { getIcommerceUnitSystemName, getLengthCode } from '@hub/bearing-hub/common/useUnitSystem/useUnitSystem';
import type { LayoutData } from '@hub/page-helpers/getInitialLayoutData';

export type FreeFormHubResultProps = {
  layout: LayoutData;
  contact: NodeContact | null;
};

const DynamicModelViewer = dynamic(
  () => import('@hub/common/ModelViewer/ModelViewer').then(mod => ({ default: mod.ModelViewer })),
  { ssr: false }
);

export const FreeFormHubResult = ({ layout, contact }: FreeFormHubResultProps) => {
  const session = useSessionStore(state => state.session);
  const { t, i18n } = useTranslation(['free-form-hub/freeFormHub', 'common']);
  const [clientReady, setClientReady] = useState(false);
  const [modelThumbnail, setModelThumbnail] = useState<string>();
  const router = useRouter();
  const { query, locale } = router;
  const { embedded, showSampleModal, ...newQuery } = query;
  const [quantity, setQuantity] = useState(DEFAULT_QUANTITY);
  const [showSampleModels, setShowSampleModels] = useState(!!showSampleModal && !embedded);
  const fileId = embedded as string;
  const { upload, uploadedFileId, uploadStatus, checkUploadStatus } = useFfhUpload();
  const { products, metaData, dataIsLoading, productsOrigins } = useStreamProductsData(
    session,
    fileId,
    quantity,
    uploadStatus,
    clientReady
  );
  const [unitSystem, setSetUnitSystem] = useState(UnitSystem.metric);
  const productList = useFilteredProductList(products ?? [], router.query as freeFormFilterValues, unitSystem);
  const { materials, procedure, materialNames } = useMaterials(products);
  const selectedMaterialColor = useSelectedMaterialColor(materials, router);
  const { showLoader, hideLoader } = useLoader();
  const [noFileToShow, setNoFileToShow] = useState<boolean>(router.isReady && !fileId);
  const [newFile, setNewFile] = useState<File | null>(null);
  const [currentPageLocation, setCurrentPageLocation] = useState('');
  const [showRecommendations, setShowRecommendations] = useState<boolean>(false);
  const [activeIglideStandardModal, setActiveIglideStandardModal] = useState(false);
  const { ibdRecommendation, igeRecommendation, fileDimensions, resetRecommendations } = useNbsRecommendation(
    fileId,
    quantity,
    clientReady,
    router.query
  );
  const [showFilterReset, setShowFilterReset] = useState(false);
  const [filterCount, setFilterCount] = useState(0);
  const { CustomEvent } = usePiwikPro();
  const { copyLinkToClipboard } = useCopyLinkToClipboard(
    t('BUY_BOX.SHARE_LINK_TOAST_SUCCESS', { ns: 'free-form-hub/freeFormHubDetails' })
  );
  const { addToast } = useToasts();
  const { materialsLifetime, maxLifeTimeReference, currentLifeTimeConfiguration, fetchLifetimeForMaterials } =
    useLifeTime();
  const navigateToMaterialDetails = (productId: string) => {
    showLoader();
    void router.push(
      {
        pathname: '/free-form-hub/[id]',
        query: { id: productId, ...newQuery },
      },
      undefined,
      { shallow: true }
    );
    CustomEvent.trackEvent(
      ffhEventCategory,
      TrackedFfhEventActions.articleSelect,
      JSON.stringify({ articleName: productId, filters: router.query })
    );
  };
  const { loadSampleModel } = useSampleModel({ onDrop: file => setNewFile(file) });

  const newQuantityIsValid = (newQuantity: number) => newQuantity > 0 && newQuantity <= QUANTITY_LIMIT;

  const resetRecommendationState = () => {
    setShowRecommendations(false);
    resetRecommendations();
  };

  useEffect(() => {
    if (query.quantity) {
      const newValue = newQuantityIsValid(Number(query.quantity)) ? Number(query.quantity) : DEFAULT_QUANTITY;
      setQuantity(newValue);
    }
    void checkUploadStatus(fileId, 0, 0);
  }, [router.isReady]);

  useEffect(() => {
    if (SUPPORTED_IGLIDE_IBH_LOCALES.includes(locale)) {
      setSetUnitSystem(UnitSystem.imperial);
    } else {
      setSetUnitSystem(UnitSystem.metric);
    }
  }, [locale]);

  useEffect(() => {
    if (noFileToShow) {
      hideLoader();
    }
  }, [noFileToShow]);

  useEffect(() => {
    if (router.isReady) {
      setNoFileToShow(!fileId);
      if (!fileId) {
        resetRecommendationState();
      }
    }
  }, [fileId, router.isReady]);

  useEffect(() => {
    if (quantity !== DEFAULT_QUANTITY) {
      void router.replace({ query: { ...query, quantity } }, undefined, { shallow: true });
    }
    CustomEvent.trackEvent(ffhEventCategory, TrackedFfhEventActions.quantity, quantity.toString());
  }, [quantity]);

  useEffect(() => {
    if (newFile && newFile.name) {
      void upload(newFile);
      setNoFileToShow(false);
    }
  }, [newFile]);

  useEffect(() => {
    if (uploadedFileId) {
      CustomEvent.trackEvent(ffhEventCategory, TrackedFfhEventActions.uploadModelSuccess);
      setShowSampleModels(false);
      resetRecommendationState();
      void router.push(
        {
          pathname: router.pathname,
          query: { ...query, embedded: uploadedFileId },
        },
        undefined,
        { shallow: true }
      );
    }
  }, [uploadedFileId]);

  useEffect(() => {
    if ((ibdRecommendation && ibdRecommendation.success) || igeRecommendation) {
      setShowRecommendations(true);
    }
  }, [ibdRecommendation, igeRecommendation]);

  useEffect(() => {
    setCurrentPageLocation(window.location.origin);
  }, [session]);

  useEffect(() => {
    const filterValues = Object.values(freeFormHubFilterKeys).filter(key => newQuery[key]);
    setShowFilterReset(filterValues.length > 0);
    setFilterCount(filterValues.length);
  }, [newQuery]);

  useEffect(() => {
    void fetchLifetimeForMaterials(
      currentLifeTimeConfiguration,
      materialNames,
      query,
      SpatialTypeOfMovementSingle.linear
    );
  }, [materialNames]);

  useEffect(() => {
    if (session) {
      setClientReady(true);
    }
  }, [session, router.isReady]);

  const resetSideBarFilters = () => {
    const updatedQuery = { ...router.query };
    Object.values(freeFormHubFilterKeys).forEach(key => {
      delete updatedQuery[key];
    });
    void router.replace(
      {
        pathname: router.pathname,
        query: updatedQuery,
      },
      undefined,
      { shallow: true, scroll: false }
    );
  };

  const showToast = (product: Product) => {
    addToast({
      content: (
        <div className="flex flex-row items-center">
          <div className="mr-4">
            <Icon size={9} id={IconID.INFO_CIRCLE_MODERN_FILLED} color={IconColor.WHITE} />
          </div>

          <div className="flex-col">
            <p className="mb-2 font-normal text-xl">
              {t('NOTIFICATION.ADD_TO_CART_SUCCESS', {
                quantity: quantity || 1,
                material: t(`MATERIALS.${product.material.name}_NAME`, { ns: 'free-form-hub/materials' }),
                ns: 'free-form-hub/freeFormHub',
              })}
            </p>
            {product.price.unit !== -1 && (
              <>
                <p className="font-semibold text-xl">
                  {displayPrice(product.price.unit, product.price.currency, i18n.language)}/
                  {t('NOTIFICATION.UNIT_PRICE', { ns: 'free-form-hub/freeFormHub' })}
                </p>
                <p className="font-semibold text-xl">
                  {displayPrice(product.price.total, product.price.currency, i18n.language)}/
                  {t('NOTIFICATION.TOTAL_PRICE', { ns: 'free-form-hub/freeFormHub' })}
                </p>
              </>
            )}
          </div>
        </div>
      ),
      type: ToastType.INFO,
      position: 'relative top-72 mb-2 left-[calc(100vw-36rem)] w-[35rem]' as ToastPosition,
      allowMultiple: true,
    });
  };

  const dimensionsSection = () => {
    return (
      <div className="flex flex-col  justify-between text-sm mt-4 md:pl-10 md:mt-0">
        <span className="text-3xl font-bold">
          {t(`HEADLINES.DIMENSIONS`, {
            ns: 'free-form-hub/freeFormHub',
          })}
        </span>
        <div>
          <p className="font-bold">
            {' '}
            {t(`COMMON.SIZE`, {
              ns: 'free-form-hub/freeFormHubCommon',
            })}
          </p>
          <p>
            {fromMetricDistanceMilli(unitSystem, metaData.boundingBox[0]).toLocaleString(locale)}{' '}
            {getUnitLabelForUnitSystem(LengthCode.MILLIMETER, getIcommerceUnitSystemName(unitSystem))} x{' '}
            {fromMetricDistanceMilli(unitSystem, metaData.boundingBox[1]).toLocaleString(locale)}{' '}
            {getUnitLabelForUnitSystem(LengthCode.MILLIMETER, getIcommerceUnitSystemName(unitSystem))} x{' '}
            {fromMetricDistanceMilli(unitSystem, metaData.boundingBox[2]).toLocaleString(locale)}{' '}
            {getUnitLabelForUnitSystem(LengthCode.MILLIMETER, getIcommerceUnitSystemName(unitSystem))}
          </p>
        </div>
        <div>
          <p className="font-bold">
            {t(`COMMON.UNITS`, {
              ns: 'free-form-hub/freeFormHubCommon',
            })}
          </p>
          <p>
            {unitSystem === UnitSystem.metric ? 'Millimeter' : 'inch'} [{getLengthCode(unitSystem)}]
          </p>
        </div>
        <div>
          <p className="font-bold">
            {t(`COMMON.VOLUME`, {
              ns: 'free-form-hub/freeFormHubCommon',
            })}
          </p>
          <p>
            {fromMetricVolumeMilli(unitSystem, metaData.volume * 1000).toLocaleString(locale)}{' '}
            {getUnitLabelForUnitSystem(AreaCode.CUBIC_MILLIMETER, getIcommerceUnitSystemName(unitSystem))}
          </p>
        </div>
      </div>
    );
  };

  const modelViewer = (
    <div className="flex flex-col md:flex-row">
      {metaData && productsOrigins.length ? (
        <div className="h-[200px] md:w-[300px] w-full">
          <DynamicModelViewer
            configuration={{
              width: 300,
              height: 200,
              materialColor: selectedMaterialColor,
              modelUrl: metaData.link,
              fileType: supportedFileFormat.GLB,
            }}
            onModelThumbnailGenerate={thumbnail => {
              if (!modelThumbnail) {
                setModelThumbnail(thumbnail);
              }
            }}
          />
        </div>
      ) : (
        <div className="h-[200px] w-[300px] flex justify-center items-center bg-gray-50">
          <span> {t(`COMMON.LOADING`, { ns: 'free-form-hub/freeFormHubCommon' })}</span>
        </div>
      )}
      {metaData && dimensionsSection()}
    </div>
  );

  const collapsibleSection = (
    <div className="hidden lg:block mb-6 mb-16">
      <Collapsible
        disableScrolling
        activeTab={1}
        styles={{ padding: '5px 15px' }}
        theme={CollapsibleTheme.ORANGE}
        keepContentInDom
        items={[
          {
            title: t(`CALCULATE_SERVICE_LIFE`, { ns: 'free-form-hub/lifeTime' }),
            content: (
              <LifetimeParametersForm
                currentLifeTimeConfiguration={currentLifeTimeConfiguration}
                onCalculate={values =>
                  fetchLifetimeForMaterials(values, materialNames, query, SpatialTypeOfMovementSingle.linear)
                }
                unitSystem={unitSystem}
                isEmbedded
              />
            ),
          },
        ]}
      />
    </div>
  );

  const quantityInput = (
    <div className="flex flex-col max-w-md mb-5 lg:mb-0 mt-10 lg:flex-row justify-end lg:-mt-10">
      <div className="">
        <div className="mb-4 font-bold text-2xl leading-xl">
          {t('BUY_BOX.QUANTITY', { ns: 'free-form-hub/freeFormHubDetails' })}
        </div>
        <BasicNumberInput
          id="free-form-hub-quantity"
          value={quantity.toString()}
          onChange={value => setQuantity(Number(value))}
          validator={(inputValue: string) => Number(inputValue) > 0 && Number(inputValue) <= QUANTITY_LIMIT}
          errorMessage={t('COMMON.QUANTITY_VALIDATION', {
            ns: 'free-form-hub/freeFormHubCommon',
            min: 1,
            max: QUANTITY_LIMIT,
          })}
          onlyInt
        />
      </div>
    </div>
  );

  const productsControlsSections = (
    <div className="mt-12 mb-8 flex flex-col lg:flex-row justify-between">
      <div className="flex flex-col">
        <div className="flex gap-y-4 flex-col md:flex-row md:items-center">
          <h3 className="text-3xl font-semibold text-black">
            {t(`HEADLINES.CHOOSE_FROM_PRODUCTS`, {
              count: productList?.length || 0,
              ns: 'free-form-hub/freeFormHub',
            })}
          </h3>
          <button
            className="md:ml-6 flex items-center lg:justify-center text-igus-primary"
            type="button"
            onClick={async () => {
              CustomEvent.trackEvent(ffhEventCategory, TrackedFfhEventActions.shareLink);
              const url = currentPageLocation + router.asPath;
              await copyLinkToClipboard(url);
            }}
          >
            <Icon size={6} color={IconColor.ORANGE} id={IconID.SHARE} />
            <span className="ml-4">{t(`SHARE_CONFIG_TEXT_LINK`, { ns: 'free-form-hub/freeFormHub' })}</span>
          </button>
        </div>
        {metaData && metaData.size > CNC_FILE_SIZE_LIMIT && (
          <p className="mt-1 text-gray-300">
            {t(`NO_CNC_RESULTS`, {
              ns: 'free-form-hub/freeFormHub',
            })}
          </p>
        )}
      </div>
      <div className="md:block hidden">{quantityInput}</div>
    </div>
  );

  const headSection = (
    <>
      <h1 className="my-16 leading-none lg:text-[5.6rem] text-[3.2rem] font-semibold text-black">
        {t(`HEADLINES.LEAD`, { ns: 'free-form-hub/freeFormHub' })}
      </h1>
      <div>
        <span>
          {t('HOW_IT_WORKS.TITLE', {
            ns: 'free-form-hub/freeFormHub',
          })}
        </span>
        <ol className="list-decimal pt-4 pl-16">
          {[1, 2, 3].map(index => (
            <li key={index}>
              {t(`HOW_IT_WORKS.STEP_${index}`, {
                ns: 'free-form-hub/freeFormHub',
              })}
            </li>
          ))}
        </ol>
        <p className="mt-4">
          {t('HOW_IT_WORKS.DESCRIPTION', {
            ns: 'free-form-hub/freeFormHub',
          })}
        </p>
      </div>
      <div className="mb-20 mt-10">
        <span className="font-bold">
          {t('ADDITIONAL_SERVICE.TITLE', {
            ns: 'free-form-hub/freeFormHub',
          })}
        </span>
        <p>
          {t('ADDITIONAL_SERVICE.DESCRIPTION', {
            ns: 'free-form-hub/freeFormHub',
          })}
        </p>
      </div>
      {noFileToShow && <UploadProcessImages />}
    </>
  );

  const nothingFound = () => (
    <div>
      <span data-testid="nothing-found-text">
        {t(`COMMON.OVERVIEW_NO_MATCHES_TEXT`, { ns: 'free-form-hub/freeFormHubCommon' })}
      </span>
      <Link
        flex={false}
        text={t('COMMON.OVERVIEW_NO_MATCHES_LINK_DESCRIPTION', { ns: 'free-form-hub/freeFormHubCommon' })}
        href={EXTERNAL_LINKS.INQUIRY_INDIVIDUAL_COMPONENT(locale)}
        primary
        external
      />
    </div>
  );

  const resetFilterButton = showFilterReset && (
    <div className="flex">
      <FilterResetLink
        text={t('PRODUCT_OVERVIEW.FILTER.REMOVE_ALL_FILTERS', { ns: 'category/productFilter' })}
        dataTestId="remove_all_filters"
        reset={() => {
          CustomEvent.trackEvent(ffhEventCategory, TrackedFfhEventActions.removeAllFiltersSelect);
          resetSideBarFilters();
        }}
      />
      <span className="ml-2">
        ({filterCount} {t(`COMMON.ACTIVE`, { ns: 'bearing-hub/bearingHub' })})
      </span>
    </div>
  );

  const offer = (
    <div className="flex flex-col lg:flex-row justify-end">
      <div className="mb-8 lg:w-3/12 lg:mr-8 lg:mb-0">
        <FilterForm
          disabled={false}
          materials={materials}
          procedure={procedure}
          unitSystem={unitSystem}
          activeIglideStandardModal={activeIglideStandardModal}
          setActiveIglideStandardModal={setActiveIglideStandardModal}
        />
      </div>
      <div className="lg:w-9/12">
        {productList?.length > 0 && (
          <>
            <div className="md:block hidden">
              {dataIsLoading && (
                <ProgressBar
                  progressBarText={t('COMMON.SELECTION_LOADING', {
                    current: productsOrigins.length,
                    ns: 'free-form-hub/freeFormHubCommon',
                  })}
                  currentProgressCount={productsOrigins.length}
                  maxProgressCount={3}
                />
              )}
              <FreeFormHubTable
                metaData={metaData}
                productList={productList}
                fileId={fileId}
                maxLifeTimeReference={maxLifeTimeReference}
                materialsLifetime={materialsLifetime}
                currentLifeTimeConfiguration={currentLifeTimeConfiguration}
                onNavigateToMaterialDetails={row => navigateToMaterialDetails(row.id)}
                quantity={quantity}
                modelThumbnail={modelThumbnail}
                showToast={showToast}
                unitSystem={unitSystem}
              />
            </div>
            <div className="md:hidden block">
              {quantityInput}
              {dataIsLoading && (
                <ProgressBar
                  progressBarText={t('COMMON.SELECTION_LOADING', {
                    current: productsOrigins.length,
                    ns: 'free-form-hub/freeFormHubCommon',
                  })}
                  currentProgressCount={productsOrigins.length}
                  maxProgressCount={3}
                />
              )}
              <FreeFormHubProductTile
                productList={productList}
                fileId={fileId}
                quantity={quantity}
                modelThumbnail={modelThumbnail}
                metaData={metaData}
                onNavigateToMaterialDetails={productId => navigateToMaterialDetails(productId)}
                showToast={showToast}
              />
            </div>
          </>
        )}
        {dataIsLoading || (productList?.length === 0 && nothingFound())}
      </div>
    </div>
  );

  return (
    <BaseLayout
      breadcrumbItems={[
        {
          title: t('HEADLINES.APP_NAME', { ns: 'free-form-hub/freeFormHub' }),
          url: `/${FREE_FORM_HUB_URL}`,
          key: `/${FREE_FORM_HUB_URL}`,
          isExternal: true,
        },
      ]}
      contact={contact}
      layout={layout}
    >
      <SeoMeta
        metaTitle={t('HEADLINES.APP_NAME', { ns: 'free-form-hub/freeFormHub' })}
        categoryName="free-form=hub"
        languageAlternates={[]}
        totalPages={1}
        productImage={null}
        noIndexState
        noFollow
      />
      <div>
        {headSection}
        <SampleDialog
          visible={showSampleModels}
          onClose={() => setShowSampleModels(false)}
          onLoadSampleModel={formattedSampleNumber => loadSampleModel(formattedSampleNumber)}
        />
        <DropZone
          clientReady={clientReady}
          onShowSampleModel={() => setShowSampleModels(true)}
          onDrop={file => setNewFile(file)}
          fullScreen={!noFileToShow}
        />
        {!noFileToShow && (
          <>
            {modelViewer}
            {collapsibleSection}
            {productsControlsSections}
            <NbsRecommendationDialog
              visible={showRecommendations}
              ibdRecommendation={ibdRecommendation}
              igeRecommendation={igeRecommendation}
              quantity={quantity}
              session={session}
              fileDimensions={fileDimensions}
              onClose={resetRecommendationState}
            />
            <div className="hidden lg:flex mb-3">
              <div>{resetFilterButton}</div>
            </div>
            {offer}
            <IglideStandardModal
              header={t(`HEADLINES.IGLIDE_STANDARDS_MODAL`, { ns: 'bearing-hub/bearingHub' })}
              activeModal={activeIglideStandardModal}
              setActiveModal={setActiveIglideStandardModal}
            />
          </>
        )}
      </div>
    </BaseLayout>
  );
};
